<template>
  <button
    class="margin-top-xs"
    :class="buyNow ? 'link-sm no-text-decoration' : 'button secondary expanded'"
    @click="mutate"
  >
    {{ title }}
  </button>
</template>

<script setup>
import {
  unref, watchEffect, markRaw, defineAsyncComponent,
} from 'vue';
import { brand } from 'config';
import { useProduct } from '@modules/product';
import { useFeatureEligibility } from '@modules/featureControl';
import { useProductData } from '@composables/useProductData';
import { useEnvProps } from '@composables/useEnvProps';
import { useSlideout } from '@composables/utils/useSlideout';
import { useBrand } from '@composables/useBrand';
import { useAddToRegistry } from '../composables';

const props = defineProps({
  /**
   * The payload to send to the add to registry endpoint.
   * @type {import('vue').Prop<AddToRegistryPayload>}
   */
  payload: {
    type: Object,
    required: true,
  },
  payloadValidator: {
    /**
     * @type {import('vue').PropType<AddToRegistryValidator>}
     */
    type: Function,
    default: () => null,
  },
});

const { hmacKey, atrXmacysApiKey, tagMngrEnv } = useEnvProps();
const { isMcom } = useBrand();
const { buyNow } = useFeatureEligibility();
const { productId } = useProduct();
const productData = useProductData(productId);

const { showSlideout } = useSlideout();

const unrefBuyNow = unref(buyNow);

// registry title
const title = (isMcom && !unrefBuyNow) ? 'Add To Registry' : 'Add to registry';

const slideoutOnSuccess = () => showSlideout({
  id: `add-to-registry-${productId}`,
  title: 'Add to Registry',
  component: markRaw(
    defineAsyncComponent(() => import(/* webpackChunkName: "AddToRegistry" */ '@common-vue/add-to-registry/src/components/Registry')),
  ),
  props: {
    registryProperty: productData,
    tagMngrEnv,
    brand,
    hmac: hmacKey,
    macysXapiKey: atrXmacysApiKey,
    deviceType: 'SITE', // TODO update deviceType
    ...props.payload,
    // @close: closeSlideout // TODO confirm if @close exists or if it will exist in the future
  },
});

const { mutate, error } = useAddToRegistry(props.payload, {
  payloadValidator: props.payloadValidator,
  onSuccess: slideoutOnSuccess,
});

const emits = defineEmits(['error']);

watchEffect(() => {
  emits('error', error.value);
});

</script>

<style lang="scss" scoped>
.no-text-decoration {
  text-transform: none;
}
</style>
